import { useEffect, useState } from "react";
import ComponentCard from "../common/ComponentCard";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Select from "../form/Select";
import TextArea from "../form/input/TextArea";
import { ShoppingCart, Monitor, Smartphone } from "lucide-react";
import Switch from "../form/switch/Switch";
import Button from "../ui/button/Button";
import { useNavigate } from "react-router";
import React from "react";
import axios from "axios";
import { authService } from "../../services/authService";
import { getCurrencySymbol } from "../../utils/currencyUtils";

// Função para formatar moeda no padrão brasileiro
const formatCurrency = (value: string): string => {
    if (!value) return '';
    value = value.replace(/\D/g, '');
    const amount = parseFloat(value) / 100;
    return amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Função para tornar qualquer cor super clara (para o fundo do card)
const getSuperLightColor = (hexColor: string): string => {
    try {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
        if (!result) return '#f8f8f8';
        let r = parseInt(result[1], 16);
        let g = parseInt(result[2], 16);
        let b = parseInt(result[3], 16);
        const factor = 0.95;
        r = Math.floor(255 - ((255 - r) * (1 - factor)));
        g = Math.floor(255 - ((255 - g) * (1 - factor)));
        b = Math.floor(255 - ((255 - b) * (1 - factor)));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    } catch (e) {
        return '#f8f8f8';
    }
};

// Função para tornar qualquer cor clara (para o fundo do checkbox)
const getLightColor = (hexColor: string): string => {
    try {
        // Verificar se é uma cor hexadecimal válida
        if (!hexColor || hexColor.length < 7) return 'rgba(0, 0, 0, 0.3)';

        // Converter a cor hexadecimal para RGB
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);

        // Criar uma versão clara (70% de branco)
        return `rgba(${r}, ${g}, ${b}, 0.3)`;
    } catch (error) {
        return 'rgba(0, 0, 0, 0.3)';
    }
};

// Função para converter URL do YouTube para formato embed
const convertYoutubeUrl = (url: string): string => {
    if (!url) return '';

    // Verificar se é uma URL do YouTube
    if (url.includes('youtube.com/watch?v=')) {
        // Extrair o ID do vídeo
        const videoId = url.split('v=')[1]?.split('&')[0];
        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
        }
    } else if (url.includes('youtu.be/')) {
        // Extrair o ID do vídeo de URLs encurtadas
        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
        }
    }

    // Se não for uma URL do YouTube ou já estiver no formato embed, retornar a URL original
    return url;
};

// Funções utilitárias para cores (versão simplificada)
const hexToRgb = (hex: string): { r: number, g: number, b: number } => {
    hex = hex.replace('#', '');
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return { r, g, b };
};

const rgbToHex = ({ r, g, b }: { r: number, g: number, b: number }): string => {
    const toHex = (c: number) => {
        const hex = Math.max(0, Math.min(255, c)).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Versões simplificadas das funções de conversão HSV
const rgbToHsv = ({ r, g, b }: { r: number, g: number, b: number }): { h: number, s: number, v: number } => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s, v = max;
    const d = max - min;
    s = max === 0 ? 0 : d / max;
    if (max !== min) {
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return { h: h * 360, s, v };
};

const hsvToRgb = ({ h, s, v }: { h: number, s: number, v: number }): { r: number, g: number, b: number } => {
    h /= 360;
    let r, g, b;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0: r = v; g = t; b = p; break;
        case 1: r = q; g = v; b = p; break;
        case 2: r = p; g = v; b = t; break;
        case 3: r = p; g = q; b = v; break;
        case 4: r = t; g = p; b = v; break;
        case 5: r = v; g = p; b = q; break;
        default: r = 0; g = 0; b = 0;
    }
    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
    };
};

interface OrderbumpFormData {
    name: string;
    offerTitle: string;
    description: string;
    salePrice: string;
    promoPrice: string;
    ctaTitle: string;
    active: boolean;
    global: boolean;
    purchasingId: string;
    purchaseVariantId: string;
    offerId: string;
    offerVariantId: string;
    videoUrl: string;
    videoActive: boolean;
}

interface NewOrderbumpFormProps {
    productId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

// Componente de Preview
interface OrderbumpPreviewProps {
    formValues: OrderbumpFormData;
    selectedProduct: any;
    primaryColor: string;
    colorText: string;
    currencySymbol: string;
}

const OrderbumpPreview: React.FC<OrderbumpPreviewProps> = ({
    formValues,
    selectedProduct,
    primaryColor,
    colorText,
    currencySymbol
}) => {
    const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

    // Valores para exibir no preview
    const offerTitle = formValues.offerTitle || 'Título da Oferta';
    const description = formValues.description || 'Descrição da oferta';
    const salePrice = formValues.salePrice || '0,00';
    const promoPrice = formValues.promoPrice || '0,00';
    const productName = selectedProduct?.name || 'Nome do produto';
    const productImage = selectedProduct?.imageUrl || undefined;
    const ctaText = formValues.ctaTitle || 'Texto do CTA';

    return (
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] p-4 sticky top-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Preview do Orderbump</h3>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setPreviewMode('desktop')}
                        className="p-2 rounded-md bg-gray-100 dark:bg-gray-800"
                        style={previewMode === 'desktop' ? { backgroundColor: primaryColor, color: 'white' } : {}}
                    >
                        <Monitor size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setPreviewMode('mobile')}
                        className="p-2 rounded-md bg-gray-100 dark:bg-gray-800"
                        style={previewMode === 'mobile' ? { backgroundColor: primaryColor, color: 'white' } : {}}
                    >
                        <Smartphone size={16} />
                    </button>
                </div>
            </div>

            {/* Card principal */}
            <div
                className={`p-4 rounded-lg transition-all duration-300 w-full ${previewMode === 'mobile' ? 'mx-auto' : ''}`}
                style={{
                    border: `2px dashed ${primaryColor}`,
                    maxWidth: previewMode === 'mobile' ? '320px' : '100%',
                    backgroundColor: getSuperLightColor(primaryColor),
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
            >
                <h3
                    className="text-start font-semibold mb-2"
                    style={{ color: primaryColor }}
                >
                    {offerTitle}
                </h3>

                <p className="text-start text-sm mb-4 text-gray-800" style={{ whiteSpace: 'pre-line' }}>
                    {description}
                </p>

                <hr className="my-3 border-gray-300 dark:border-gray-700" />

                <div className={`grid grid-cols-1 ${previewMode === 'mobile' ? 'gap-4' : 'md:grid-cols-2 gap-4'} mb-4`}>
                    {/* Coluna 1 - Produto */}
                    <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded flex items-center justify-center">
                            {productImage ? (
                                <img
                                    src={productImage}
                                    alt={productName}
                                    className="w-full h-full object-cover rounded"
                                />
                            ) : (
                                <div className="w-full h-full bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                                    <ShoppingCart size={24} className="text-gray-500 dark:text-gray-400" />
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col justify-center">
                            <h4 className="font-semibold text-gray-800">
                                {productName}
                            </h4>

                            <div className="flex flex-col md:flex-row gap-1">
                                {promoPrice && promoPrice !== '0,00' ? (
                                    // Mostrar "de/por" quando houver preço promocional
                                    <>
                                        {salePrice && salePrice !== '0,00' && (
                                            <p className="text-sm text-gray-500">de <span className="line-through text-gray-500">
                                                {currencySymbol} {salePrice}
                                            </span>
                                            </p>
                                        )}
                                        {' '}
                                        <p className="text-sm text-gray-500">por <span className="font-bold" style={{ color: primaryColor }}>
                                            {currencySymbol} {promoPrice}
                                        </span>
                                        </p>
                                    </>
                                ) : (
                                    // Mostrar apenas o preço em azul quando não houver preço promocional
                                    <p className="text-sm">
                                        <span className="font-bold" style={{ color: primaryColor }}>
                                            {currencySymbol} {salePrice || '0,00'}
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Coluna 2 - Vídeo (apenas se videoActive for true) */}
                    {formValues.videoActive && (
                        <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-2">
                            {formValues.videoUrl ? (
                                <div className="w-full aspect-video">
                                    <iframe
                                        src={convertYoutubeUrl(formValues.videoUrl)}
                                        className="w-full h-full rounded"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-4 text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mb-2 text-gray-400">
                                        <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                    </svg>
                                    <p className="text-sm text-gray-500">Seu vídeo aparece aqui</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Checkbox com fundo mais claro */}
                <div
                    className="mt-4 p-2 rounded"
                    style={{
                        backgroundColor: getLightColor(primaryColor),
                        border: `1px solid ${primaryColor}`
                    }}
                >
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            className="w-4 h-4"
                            style={{
                                accentColor: primaryColor,
                                borderColor: primaryColor
                            }}
                        />
                        <span style={{ color: colorText }}>
                            {ctaText}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente de seletor de cores
interface ColorPickerProps {
    color: string;
    onChange: (color: string) => void;
    label: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [rgbValues, setRgbValues] = useState({ r: 0, g: 0, b: 0 });
    const [hue, setHue] = useState(0);

    // Inicializar valores RGB a partir da cor hex quando o componente é montado ou a cor muda
    useEffect(() => {
        try {
            const rgb = hexToRgb(color);
            setRgbValues(rgb);
            const hsv = rgbToHsv(rgb);
            setHue(hsv.h);
        } catch (e) {
            console.error("Erro ao converter cor:", e);
        }
    }, [color]);

    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        onChange(inputValue);

        // Tentativa de validar e corrigir a cor
        let newColor = inputValue;

        // Adicione # se não tiver e for um valor hexadecimal válido
        if (!newColor.startsWith('#') && /^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newColor)) {
            newColor = '#' + newColor;
        }

        // Verifique se é uma cor hexadecimal válida
        if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newColor)) {
            // Atualizar valores RGB
            try {
                const rgb = hexToRgb(newColor);
                setRgbValues(rgb);
                // Atualizar valor de matiz
                const hsv = rgbToHsv(rgb);
                setHue(hsv.h);
            } catch (e) {
                console.error("Erro ao converter cor:", e);
            }
        }
    };

    const handleColorBlur = () => {
        if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
            let cleanColor = color.replace(/[^A-Fa-f0-9]/g, '');

            if (cleanColor.length === 3 || cleanColor.length === 6) {
                cleanColor = '#' + cleanColor;
                onChange(cleanColor);

                try {
                    const rgb = hexToRgb(cleanColor);
                    setRgbValues(rgb);
                    const hsv = rgbToHsv(rgb);
                    setHue(hsv.h);
                } catch (e) {
                    onChange('#FF3E1C');
                    setRgbValues({ r: 255, g: 62, b: 28 });
                }
            } else {
                onChange('#FF3E1C');
                setRgbValues({ r: 255, g: 62, b: 28 });
            }
        }
    };

    const handleGradientClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

        const hsv = { h: hue, s: x, v: 1 - y };
        const rgb = hsvToRgb(hsv);

        setRgbValues(rgb);
        onChange(rgbToHex(rgb));
    };

    const handleColorSelectorDrag = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.buttons !== 1) return;
        handleGradientClick(e);
    };

    const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newHue = Number(e.target.value);
        setHue(newHue);

        const hsv = rgbToHsv(rgbValues);
        const newRgb = hsvToRgb({ h: newHue, s: hsv.s, v: hsv.v });

        setRgbValues(newRgb);
        onChange(rgbToHex(newRgb));
    };

    const handleRgbChange = (component: 'r' | 'g' | 'b') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        if (isNaN(value)) return;

        // Limitar valores entre 0 e 255
        const validValue = Math.max(0, Math.min(255, value));
        const newRgbValues = { ...rgbValues, [component]: validValue };
        setRgbValues(newRgbValues);

        // Atualizar cor hexadecimal
        const newColor = rgbToHex(newRgbValues);
        onChange(newColor);

        // Atualizar o matiz
        const hsv = rgbToHsv(newRgbValues);
        setHue(hsv.h);
    };

    return (
        <div className="relative">
            <Label>{label}</Label>
            <div className="flex items-center gap-2">
                <div
                    className="w-8 h-8 rounded border border-gray-300 dark:border-gray-700 cursor-pointer"
                    style={{ backgroundColor: color }}
                    onClick={() => setIsOpen(!isOpen)}
                />
                <Input
                    type="text"
                    value={color}
                    onChange={handleColorChange}
                    onBlur={handleColorBlur}
                />
            </div>

            {isOpen && (
                <div className="absolute z-10 mt-2 p-4 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-64">
                    {/* Gradiente de cor */}
                    <div
                        className="w-full h-32 rounded-lg mb-4 relative cursor-crosshair"
                        style={{
                            background: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, #000 100%),
                                        linear-gradient(to right, #fff 0%, hsl(${hue}, 100%, 50%) 100%)`
                        }}
                        onClick={handleGradientClick}
                        onMouseMove={handleColorSelectorDrag}
                    >
                        {/* Seletor de cor */}
                        <div
                            className="absolute w-4 h-4 rounded-full border-2 border-white shadow-md transform -translate-x-1/2 -translate-y-1/2"
                            style={{
                                backgroundColor: color,
                                top: `${100 - (rgbToHsv(rgbValues).v * 100)}%`,
                                left: `${rgbToHsv(rgbValues).s * 100}%`
                            }}
                        />
                    </div>

                    {/* Slider de matiz */}
                    <div className="mb-4">
                        <input
                            type="range"
                            min="0"
                            max="360"
                            value={hue}
                            onChange={handleHueChange}
                            className="w-full h-4 rounded-lg appearance-none cursor-pointer"
                            style={{
                                background: `linear-gradient(to right, 
                                    #f00 0%, #ff0 17%, #0f0 33%, 
                                    #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)`
                            }}
                        />
                    </div>

                    {/* Inputs RGB */}
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <Label htmlFor="r-input" className="text-xs">R</Label>
                            <Input
                                id="r-input"
                                type="number"
                                min="0"
                                max="255"
                                value={rgbValues.r}
                                onChange={handleRgbChange('r')}
                            />
                        </div>
                        <div>
                            <Label htmlFor="g-input" className="text-xs">G</Label>
                            <Input
                                id="g-input"
                                type="number"
                                min="0"
                                max="255"
                                value={rgbValues.g}
                                onChange={handleRgbChange('g')}
                            />
                        </div>
                        <div>
                            <Label htmlFor="b-input" className="text-xs">B</Label>
                            <Input
                                id="b-input"
                                type="number"
                                min="0"
                                max="255"
                                value={rgbValues.b}
                                onChange={handleRgbChange('b')}
                            />
                        </div>
                    </div>

                    <div className="mt-4 flex justify-end">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setIsOpen(false)}
                        >
                            Fechar
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function NewOrderbumpForm({ productId, onSuccess, onError }: NewOrderbumpFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<OrderbumpFormData>({
        name: "",
        offerTitle: "",
        description: "",
        salePrice: "",
        promoPrice: "",
        ctaTitle: "Adicionar ao meu pedido",
        active: true,
        global: false,
        purchasingId: "",
        purchaseVariantId: "",
        offerId: "",
        offerVariantId: "",
        videoUrl: "",
        videoActive: false
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    // Estados para controle de loading e dados
    const [products, setProducts] = useState<Array<{ value: string, label: string }>>([]);
    const [productOffers, setProductOffers] = useState<Array<{ value: string, label: string }>>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [selectedProductDetails, setSelectedProductDetails] = useState<any>(null);
    const [offersData, setOffersData] = useState<Array<any>>([]);

    // Estados para as cores
    const [primaryColor, setPrimaryColor] = useState("#465FFF");
    const [colorText, setColorText] = useState("#333333");

    // Estado para a moeda da oferta selecionada
    const [offerCurrency, setOfferCurrency] = useState("BRL");
    const [currencySymbol, setCurrencySymbol] = useState("R$");

    // Carregar produtos ativos ao carregar o componente
    useEffect(() => {
        fetchProducts();
        fetchProductOffers(); // Carrega as ofertas do produto atual
    }, []);

    // Função para buscar produtos ativos
    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products?status=active`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // O backend retorna { products: [...] }
            if (response.data && response.data.products) {
                const filteredProducts = response.data.products
                    .filter((product: any) => product.id !== productId) // Remove o produto atual
                    .map((product: any) => ({
                        value: product.id,
                        label: product.name
                    }));
                setProducts(filteredProducts);
            }
        } catch (error) {
            console.error("Erro ao carregar produtos:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    // Função para buscar ofertas de um produto específico
    const fetchProductOffers = async () => {
        try {
            setLoadingOffers(true);
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers-with-links`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Verificar o formato da resposta do backend
            if (response.data && response.data.offers) {
                // Armazenar os dados completos das ofertas para uso posterior
                setOffersData(response.data.offers);

                // Criar opções para o dropdown com as ofertas do produto
                const offerOptions = response.data.offers.map((offer: any) => {
                    // Obter o símbolo da moeda usando a função auxiliar
                    const currency = offer.currency || 'BRL';
                    const currencySymbol = getSymbolFromCurrency(currency);

                    // Formatar o preço para exibição
                    const formattedPrice = offer.price ?
                        `${currencySymbol} ${parseFloat(offer.price).toFixed(2).replace('.', ',')}` :
                        'Preço não disponível';

                    return {
                        value: offer.id,
                        label: `${offer.name} - ${formattedPrice}`
                    };
                });

                // Adicionar a opção "Qualquer oferta" no início da lista
                const allOptions = [
                    { value: 'any', label: ' Qualquer oferta' },
                    ...offerOptions
                ];

                setProductOffers(allOptions);
            }
        } catch (error) {
            console.error("Erro ao carregar ofertas do produto:", error);
        } finally {
            setLoadingOffers(false);
        }
    };

    // Função para buscar detalhes de um produto específico
    const fetchProductDetails = async (productId: string) => {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.data && response.data.product) {
                setSelectedProductDetails(response.data.product);
            }
        } catch (error) {
            console.error("Erro ao carregar detalhes do produto:", error);
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Atualizar o estado
        setFormData({ ...formData, [id]: value });
    };

    const handleTextAreaChange = (value: string) => {
        setFormData({ ...formData, description: value });
    };

    const handlePriceChange = (field: 'salePrice' | 'promoPrice') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatCurrency(e.target.value);
        const newFormData = { ...formData, [field]: formatted };
        
        // Se os preços ficaram iguais, limpar o preço promocional
        if (field === 'promoPrice' || field === 'salePrice') {
            const saleValue = field === 'salePrice' ? formatted : formData.salePrice;
            const promoValue = field === 'promoPrice' ? formatted : formData.promoPrice;
            
            if (saleValue && promoValue && saleValue === promoValue) {
                newFormData.promoPrice = '';
            }
        }
        
        setFormData(newFormData);
    };

    // Função auxiliar para obter o símbolo da moeda usando o utilitário
    const getSymbolFromCurrency = (currencyCode: string): string => {
        return getCurrencySymbol(currencyCode, 'BRL');
    };

    const handleOfferProductChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            offerId: value
        }));

        // Buscar detalhes do produto selecionado
        fetchProductDetails(value);

        // Obter a moeda da oferta selecionada a partir dos dados que já temos
        if (value !== 'any') {
            const selectedOffer = offersData.find((offer: any) => offer.id === value);
            if (selectedOffer) {
                // Obter o símbolo da moeda usando a função auxiliar
                const currency = selectedOffer.currency || 'BRL';
                const symbol = getSymbolFromCurrency(currency);

                setOfferCurrency(currency);
                setCurrencySymbol(symbol); // Usamos o symbol local para atualizar o estado currencySymbol
            }
        }
    };

    const handleOfferVariantChange = (value: string) => {
        // Se o valor for 'any', estamos lidando com a opção "Qualquer oferta"
        const isGlobal = value === 'any';

        setFormData(prev => ({
            ...prev,
            offerVariantId: isGlobal ? '' : value,
            global: isGlobal // Definir campo global baseado na seleção
        }));

        // Atualizar a moeda com base na oferta selecionada
        if (!isGlobal) {
            // Buscar a oferta selecionada nos dados que já temos
            const selectedOffer = offersData.find((offer: any) => offer.id === value);

            if (selectedOffer) {
                // Obter o símbolo da moeda usando a função auxiliar
                const currency = selectedOffer.currency || 'BRL';
                const symbol = getSymbolFromCurrency(currency);

                // Atualizar os estados
                setOfferCurrency(currency);
                setCurrencySymbol(symbol);
            }
        } else {
            // Se for "qualquer oferta", usar BRL como padrão
            setOfferCurrency('BRL');
            setCurrencySymbol(getSymbolFromCurrency('BRL'));
        }
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        setFormData({ ...formData, [id]: checked });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name) {
            newErrors.name = "Nome do orderbump é obrigatório";
        }

        if (!formData.offerId) {
            newErrors.offerId = "Produto a ser oferecido é obrigatório";
        }

        if (!formData.salePrice) {
            newErrors.salePrice = "Preço é obrigatório";
        }

        if (!formData.offerTitle) {
            newErrors.offerTitle = "Título da oferta é obrigatório";
        }

        if (!formData.description) {
            newErrors.description = "Descrição da oferta é obrigatória";
        }

        if (!formData.ctaTitle) {
            newErrors.ctaTitle = "Texto do CTA é obrigatório";
        }

        // Validar URL do vídeo se o recurso de vídeo estiver ativado
        if (formData.videoActive && !formData.videoUrl) {
            newErrors.videoUrl = "URL do vídeo é obrigatória quando o vídeo está ativado";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            // Converter valores de preço de string para número (formato brasileiro)
            const salePriceValue = parseFloat(formData.salePrice.replace(/\./g, '').replace(',', '.'));
            // Se preço promocional estiver vazio, usar o preço normal
            const promoPriceValue = formData.promoPrice ? 
                parseFloat(formData.promoPrice.replace(/\./g, '').replace(',', '.')) : 
                salePriceValue;

            // Determinar o tipo do bump com base na seleção da oferta
            const bumpType = formData.global ? 'global' : 'specific';

            // Preparar dados para envio seguindo o formato esperado pela API
            const bumpData = {
                productId: productId, // Usar productId se fornecido, caso contrário usar offerId
                productOfferId: formData.offerId, // ID do produto que será oferecido
                name: formData.name,
                description: formData.description,
                anchoringPrice: salePriceValue || null,
                offerPrice: promoPriceValue,
                ctaText: formData.ctaTitle,
                primaryColor: primaryColor,
                textColor: colorText,
                type: bumpType,
                settingsData: {
                    active: formData.active,
                    global: formData.global,
                    offerTitle: formData.offerTitle,
                    purchasingId: formData.purchasingId || null,
                    purchaseVariantId: formData.purchaseVariantId || null,
                    offerVariantId: formData.global ? null : formData.offerVariantId || null,
                    currency: offerCurrency, // Salvar a moeda da oferta para manter consistência
                    currencySymbol: currencySymbol // Salvar também o símbolo da moeda
                },
                videoUrl: convertYoutubeUrl(formData.videoUrl),
                videoActive: formData.videoActive
            };

            // Enviar dados diretamente para a API
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/bumps`,
                bumpData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.data.success) {
                setApiSuccess("Order bump criado com sucesso!");

                // Chamar o callback de sucesso, se fornecido
                if (onSuccess) {
                    onSuccess(response.data.data);
                }
            } else {
                setApiError(response.data.error || "Erro ao criar order bump");

                // Chamar o callback de erro, se fornecido
                if (onError) {
                    onError(response.data.error);
                }
            }
        } catch (error: any) {
            console.error("Erro ao criar order bump:", error);
            setApiError(error.response?.data?.error || error.message || "Erro ao criar order bump");

            // Chamar o callback de erro, se fornecido
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate(`/products/edit/${productId}?tab=bump`);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lado esquerdo - Formulário */}
            <div className="col-span-1 lg:col-span-1">
                <ComponentCard title="Criar Novo Orderbump">
                    {apiError && (
                        <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                            {apiError}
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                            {apiSuccess}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar Orderbump"
                                defaultChecked={true}
                                onChange={(checked) => handleSwitchChange("active", checked)}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="name">Nome do Orderbump *</Label>
                            <Input
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                onChange={handleInputChange}
                                value={formData.name}
                                error={!!errors.name}
                                hint={errors.name}
                            />
                        </div>

                        <div className="mt-5">
                            <Label>Ao comprar *</Label>
                            <Select
                                options={productOffers}
                                placeholder={loadingOffers ? "Carregando ofertas..." : "Selecione uma oferta"}
                                onChange={handleOfferVariantChange}
                                value={formData.global ? 'any' : formData.offerVariantId}
                                className="dark:bg-dark-900"
                            />
                            {loadingOffers && <p className="mt-1 text-sm text-gray-500">Carregando ofertas...</p>}
                            {formData.global &&
                                <p className="mt-1 text-xs text-blue-500">
                                    Este Order Bump será exibido em todas as ofertas do produto, incluindo as futuras.
                                </p>
                            }
                        </div>

                        <div className="mt-5">
                            <Label>Oferecer o produto *</Label>
                            <Select
                                options={products}
                                placeholder={loadingProducts ? "Carregando produtos..." : "Selecione um produto"}
                                onChange={(handleOfferProductChange)}
                                value={formData.offerId}
                                className={`dark:bg-dark-900 ${errors.offerId ? 'border-red-500' : ''}`}
                            />
                            {errors.offerId && <p className="mt-1 text-sm text-red-500">{errors.offerId}</p>}
                            {loadingProducts && <p className="mt-1 text-sm text-gray-500">Carregando produtos...</p>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                            <div>
                                <Label htmlFor="salePrice">Preço *</Label>
                                <Input
                                    type="text"
                                    id="salePrice"
                                    placeholder="0,00"
                                    onChange={handlePriceChange('salePrice')}
                                    value={formData.salePrice}
                                    error={!!errors.salePrice}
                                    hint={errors.salePrice}
                                />
                            </div>
                            <div>
                                <Label htmlFor="promoPrice">Preço promocional</Label>
                                <Input
                                    type="text"
                                    id="promoPrice"
                                    placeholder="0,00"
                                    onChange={handlePriceChange('promoPrice')}
                                    value={formData.promoPrice}
                                    error={!!errors.promoPrice}
                                    hint={errors.promoPrice}
                                />
                            </div>
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="offerTitle">Título da oferta *</Label>
                            <Input
                                type="text"
                                id="offerTitle"
                                placeholder="Ex: Oferta especial!"
                                onChange={handleInputChange}
                                value={formData.offerTitle}
                                error={!!errors.offerTitle}
                                hint={errors.offerTitle}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="description">Descrição *</Label>
                            <TextArea
                                value={formData.description}
                                onChange={handleTextAreaChange}
                                rows={4}
                                placeholder="Descreva os benefícios da oferta"
                                error={!!errors.description}
                                hint={errors.description}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="ctaTitle">Texto do CTA *</Label>
                            <Input
                                type="text"
                                id="ctaTitle"
                                placeholder="Ex: Adicionar ao meu pedido"
                                onChange={handleInputChange}
                                value={formData.ctaTitle}
                                error={!!errors.ctaTitle}
                                hint={errors.ctaTitle}
                            />
                        </div>

                        <div className="mt-5 grid grid-cols-1 md:grid-cols-1 gap-4">
                            <Switch
                                label="Ativar Vídeo no Orderbump"
                                defaultChecked={false}
                                onChange={(checked) => handleSwitchChange("videoActive", checked)}
                            />
                        </div>

                        {formData.videoActive && (
                            <div className="mt-5">
                                <Label htmlFor="videoUrl">URL do Vídeo *</Label>
                                <Input
                                    type="text"
                                    id="videoUrl"
                                    placeholder="Ex: https://www.youtube.com/watch?v=..."
                                    onChange={handleInputChange}
                                    error={!!errors.videoUrl}
                                    hint={errors.videoUrl}
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                            <ColorPicker
                                color={primaryColor}
                                onChange={setPrimaryColor}
                                label="Cor Primária"
                            />
                            <ColorPicker
                                color={colorText}
                                onChange={setColorText}
                                label="Cor do Texto"
                            />
                        </div>

                        <div className="mt-6 flex justify-end gap-4.5">
                            <Button
                                size="md"
                                variant="outline"
                                onClick={handleGoBack}
                            >
                                Cancelar
                            </Button>
                            <Button
                                size="md"
                                variant="primary"
                                onClick={() => handleSubmit()}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : "Adicionar Orderbump"}
                            </Button>
                        </div>
                    </form>
                </ComponentCard>
            </div>

            {/* Lado direito - Preview */}
            <div className="col-span-1">
                <OrderbumpPreview
                    formValues={formData}
                    selectedProduct={selectedProductDetails}
                    primaryColor={primaryColor}
                    colorText={colorText}
                    currencySymbol={currencySymbol}
                />
            </div>
        </div>
    );
}